import { CSSProperties } from 'react';
import { useReduxState } from 'hooks';
import { Icon } from 'components/common/icons';
import { RadioGroup } from 'components/common/form';

interface Props {
  disabled?: boolean;
  field: string;
  id: string;
  label: string;
  onChange?: () => void;
  style?: CSSProperties;
  value: any;
}

const SettingsPercentageSelector = ({
  disabled,
  field,
  id,
  label,
  onChange,
  style,
  value
}: Props) => {
  const { theme } = useReduxState();

  const options = [90, 80, 70, 60, 50, 40, 30, 20, 10, 0].map((option) => {
    // increase opacity for higher value options to create a gradient effect
    const filledOpacity = 0.3 + (option / 100) * 0.3;
    const filledColor = `rgba(0, 0, 0, ${filledOpacity})`;

    return {
      id: `${id}-${option}`,
      disabled: disabled,
      label: (
        <div className={`icon ${value >= option ? 'active' : ''}`}>
          <Icon
            className="filled"
            icon="circle"
            fixedWidth={true}
            style={{ color: filledColor }}
          />
          <Icon className="empty" icon="circle" fixedWidth={true} />
        </div>
      ),
      value: option
    };
  });

  return (
    <div className="selector" style={style}>
      <div className="label">
        <div className="label-text">{label}</div>
        <div className="label-value">{`${value}%`}</div>
      </div>

      <div className={`percentage ${disabled ? 'disabled' : ''}`}>
        <RadioGroup field={field} onChange={onChange} options={options} />
      </div>

      <style jsx>{`
        .label {
          color: ${theme.textColorLight};
          display: flex;
          font-size: 1.5rem;
          font-weight: 400;
          line-height: 1;
          opacity: ${disabled ? 0.5 : 1};
        }
        .label-text {
          flex-grow: 1;
        }
        .label-value {
          margin-left: 20px;
        }
        .percentage {
          display: inline-block;
          margin-top: 5px;
        }
        .percentage :global(.radio-group) {
          display: flex;
          flex-direction: row-reverse;
        }
        .percentage :global(.radio) {
          direction: rtl;
          display: inline-block;
        }
        .percentage :global(.radio:not(:first-child)) {
          margin-top: 0;
          padding-right: 5px;
        }
        .percentage :global(input + .label label) {
          color: #555;
          font-size: 1.8rem;
          padding-left: 0;
        }
        .percentage :global(input + .label label::before),
        .percentage :global(input + .label label::after) {
          display: none;
        }
        .percentage :global(input:focus + .label label) {
          box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.15);
        }
        .percentage :global(.icon) {
          display: inline-block;
        }
        .percentage :global(.icon .filled) {
          color: rgba(0, 0, 0, 0.4);
          display: none;
        }
        .percentage :global(.icon .empty) {
          color: rgba(0, 0, 0, 0.1);
        }
        .percentage :global(.active .filled) {
          display: inline-block;
        }
        .percentage :global(.active .empty) {
          display: none;
        }
        .percentage:not(.disabled) :global(.radio:hover .filled),
        .percentage:not(.disabled) :global(.radio:hover ~ .radio .filled) {
          display: inline-block;
        }
        .percentage:not(.disabled) :global(.radio:hover .empty),
        .percentage:not(.disabled) :global(.radio:hover ~ .radio .empty) {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default SettingsPercentageSelector;
